// ─────────────────────────────────────────────────────────────────────────────
// Todo, CD's right-rail to-do card. Populated from the aggregated open tasks
// (lib fetchTodoTasks → standalone_tasks + each release's checklist). Each row:
// task + sub-line (release name) + assigner→assignee avatar chips + due, with the
// overdue visual state and a click-through to a read-only detail popup. Empty:
// CD's "You're all caught up" note.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import type { JSX } from 'react';
import type { TodoTask } from '../../lib';
import { getCachedUser } from '../../lib/dashboard';
import { shortDueLabel } from './format';
import { ImgFill } from '../roster-sections/ImgFill';
import { TaskDetailDrawer } from '../roster-sections/TaskDetailDrawer';

export interface TodoProps {
  readonly todos: ReadonlyArray<TodoTask>;
  /** Fired after a drawer save/delete so the dashboard can refetch its tasks. */
  readonly onChanged?: () => void;
}

/** A from→to avatar chip, photo over a deterministic-hue initials circle. */
function TodoChip({ url, initials, name }: { readonly url?: string; readonly initials?: string; readonly name?: string }): JSX.Element {
  const seed = name || initials || '?';
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return (
    <span
      title={name || ''}
      style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', flex: '0 0 auto',
        fontSize: 8, fontWeight: 700, color: '#fff', fontFamily: 'var(--ac-font-mono, monospace)',
        background: `hsl(${h % 360} 42% 42%)`,
      }}
    >
      {(initials || (name || '?').slice(0, 1)).toUpperCase()}
      <ImgFill src={url || ''} alt={name || ''} />
    </span>
  );
}

export function Todo({ todos, onChanged }: TodoProps): JSX.Element {
  const hasTasks = todos.length > 0;
  const [open, setOpen] = useState<TodoTask | null>(null);
  const me = getCachedUser();
  const currentUser = me ? { id: me.id, name: me.display_name || me.email, avatarUrl: me.avatar_url, email: me.email } : null;

  return (
    <article className="card todo-card" id="todo-card">
      <div className="todo-card__head">
        <p className="eyebrow">Open tasks · Rollout + General</p>
        <h3 className="todo-card__title">To-do</h3>
      </div>

      {!hasTasks ? (
        <p className="empty-note empty-note--pad">You're all caught up, no open tasks.</p>
      ) : (
        <>
          <div className="todo-head-row">
            <span>Task</span>
            <span>Due</span>
          </div>
          <div className="todo-scroll">
            {todos.map((t) => {
              const hasFrom = !!(t.assigner || t.assignerAvatar);
              const hasPeople = hasFrom || !!(t.assignee || t.assigneeAvatar);
              return (
                <div
                  className={'todo-row todo-row--click' + (t.overdue ? ' todo-row--overdue' : '')}
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpen(t)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(t); } }}
                >
                  <div>
                    <p className="todo-row__task">{t.task}</p>
                    {t.subtitle && <p className="todo-row__sub">{t.subtitle}</p>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                    {hasPeople && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {hasFrom && <TodoChip url={t.assignerAvatar} initials={t.assignerInitials} name={t.assigner} />}
                        {hasFrom && <span style={{ color: 'var(--ac-color-t3)', fontSize: 11 }}>→</span>}
                        <TodoChip url={t.assigneeAvatar} initials={t.assigneeInitials} name={t.assignee} />
                      </span>
                    )}
                    <span className="todo-row__due">{t.due ? shortDueLabel(t.due) : '-'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {open && (
        <TaskDetailDrawer
          taskId={open.id}
          currentUser={currentUser}
          onClose={() => setOpen(null)}
          onChanged={onChanged}
        />
      )}
    </article>
  );
}
